import React, { useEffect, useState, ReactNode, useMemo } from 'react';
import { Helmet } from 'react-helmet';

import capitalize from 'lodash.capitalize';
import delve from 'dlv';
import { useDispatch, useSelector } from 'react-redux';
import { PromiseState } from 'react-refetch';
import apiConnect from 'helpers/apiConnect';
import sortSocialMetrics from 'helpers/sortSocialMetrics';
import getSocialKPIURL from 'url-constructors/getSocialKPIURL';
import styled from 'styled-components/macro';
import { ParentTopicTypes, SubTopicTypes } from 'topics';
import { trackViewedEntities } from 'redux/reducers/usage/viewed/viewedEntities';

import ErrorPage from 'pages/ErrorPage';
import TopBar from 'components/Header/TopBar/TopBar';
import Header from 'components/Header/Header';
import EntityHeader from 'components/Header/EntityHeader/EntityHeader';
import Content from 'components/styled/Content/Content';
import ErrorBoundary from 'components/utility/ErrorBoundary/ErrorBoundary';
import CountryCompareHeader from 'components/Header/CountryCompareHeader/CountryCompareHeader';
import TopicNavigation from 'components/TopicNavigation/TopicNavigation';
import ScrollTopOnMount from 'components/utility/ScrollTopOnMount/ScrollTopOnMount';
import InViewObserver from 'components/utility/InViewObserver/InViewObserver';
import BackToTop from 'components/BackToTop/BackToTop';
import Footer from 'components/Footer/Footer';

import {
  EntityContext,
  defaultColors,
  EntityTypes,
  IEntity,
  CompareModes,
} from 'context/entityContext';
import { shouldLoadEntity, loadEntity } from 'redux/reducers/entity';

import usePrevious from 'hooks/usePrevious';

import { apiTypeToEntityType } from 'helpers/mapEntityTypes';
import getPathPrefix from 'helpers/topicNavigation/getPathPrefix';
import getFiltersFromUrl from 'helpers/getFiltersFromUrl';

import EntityContent from 'pages/Entity/Content';
import { RouteComponentProps } from 'react-router';
import NotificationSettingsModal from '../components/notifications/Settings';
import getSchemas from 'schemas/componentSettings/viewSettings';
import useComponentSettings from 'hooks/useComponentSettings';
import getAllowedRelationsURL from 'url-constructors/getArtistRelationsURL';
import Breadcrumbs from 'components/Header/Breadcrumbs/Breadcrumbs';
import {
  loadCustomRegions,
  shouldLoadCustomRegions,
} from 'redux/reducers/customRegions';
import { RootState } from 'redux/rootReducer';
import { isArray } from 'lodash';

const ImgCover = styled('div')<any>`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
  display: flex;
  align-items: center;
  opacity: 0.07;
  mix-blend-mode: darken;

  img {
    width: 100%;
    min-height: -webkit-fill-available;
  }
`;

const getTopbarTitle = (
  entityType: EntityTypes,
  data: any
): ReactNode | null => {
  switch (entityType) {
    case 'track':
    case 'release':
    case 'project': {
      const displayName = delve(data, 'entity.displayName');

      if (displayName) {
        return displayName;
      }

      return null;
    }
    case 'artist':
    case 'label':
    default: {
      const displayName = delve(data, 'entity.displayName');

      if (displayName) {
        return displayName;
      }

      return null;
    }
  }
};

interface IEntityProps extends RouteComponentProps<IMatchParams> {
  compareMode: CompareModes;
  fetchSocialData: (artistIds: string) => any;
  socialData: PromiseState<any>;
  fetchArtistMetadata: (artistId: string) => any;
  artistMetadata: PromiseState<any>;
}

interface IMatchParams {
  topic: ParentTopicTypes;
  subTopic: SubTopicTypes;
  parentEntityId: string;
  entityType: EntityTypes;
  entityId: string;
  countries?: string;
}

/**
 * The Entity component renders the correct Entity component (Track, Release, Artist, Label, etc.)
 * It also renders the components that are shared between entities.
 * @param {object} props Object of properties, passed down by the parent React.Component
 * @param {object} props.match Object with routing params, passed down by BrowserRouter
 */
const Entity: React.FC<IEntityProps> = (props: IEntityProps) => {
  const {
    match,
    location,
    history,
    compareMode,
    fetchSocialData,
    socialData = PromiseState.create(),
    fetchArtistMetadata,
    artistMetadata = PromiseState.create(),
  } = props;

  const { topic, subTopic, parentEntityId, entityType, entityId, countries } =
    match.params;

  // view settings
  const schema = getSchemas();
  const [viewSettings] = useComponentSettings('viewSettings', schema);

  //Redux state
  const entitiesById = useSelector((state: any) => {
    const { entity } = state;
    return entity.byId[entityType];
  });

  // Redux dispatch
  const dispatch = useDispatch();

  // Load the entity when the entityId changes
  useEffect(() => {
    if (
      dispatch(
        shouldLoadEntity(
          entityType,
          entityId,
          entityType !== 'artist' && entityType !== 'label'
        )
      )
    ) {
      const loadMetadata =
        entityType !== 'artist' && entityType !== 'label' && !!entityId;
      dispatch(loadEntity(entityType, entityId, loadMetadata));
    }
  }, [dispatch, entityId, entityType]);

  useEffect(() => {
    if (dispatch(shouldLoadCustomRegions())) {
      dispatch(loadCustomRegions());
    }
  }, [dispatch]);

  const customRegions = useSelector(
    (state: RootState) => state.customRegions.data
  );

  const countryIds: any[] =
    countries && countries !== 'all' ? countries.split(',') : [];
  const previousCountryIds = usePrevious(countryIds);
  const [compareColors, setCompareColors] = useState(
    defaultColors.compareColors
  );
  const previousCompareColors = usePrevious(compareColors);

  //Check if we need to update the compareColors
  /** Check if an ID is removed from the previous countryIds array and return its index. */
  useEffect(() => {
    let removedIndex: number | undefined = undefined;
    if (previousCountryIds) {
      removedIndex = previousCountryIds.findIndex((item: any) => {
        return !countryIds.includes(item);
      });
    }

    /**
     * If an index is found create a new array in which the value at the removedIndex is placed at the end of the array.
     * Dont update the colors when the user removes the last item of the array.
     */

    if (
      removedIndex &&
      removedIndex > -1 &&
      removedIndex !== previousCountryIds.length - 1
    ) {
      const slicedValue = previousCompareColors[removedIndex];

      const newcompareColors = previousCompareColors.filter(
        (_: string, index: number) => {
          return index !== removedIndex;
        }
      );

      /** Place the sliced value at the next positionin the colorArray. So when the user removes the yellow item, the next item it adds will be yellow again. */
      newcompareColors.splice(previousCountryIds.length - 1, 0, slicedValue);
      setCompareColors(newcompareColors);
    }
  }, [countryIds, previousCompareColors, previousCountryIds]);

  /**
   * It seems weird to have a variable entityIds when you only have ONE entity, but the thinking here
   * is that a list of ONE or MANY items can be looped over easily. Nested components will depend on
   * a prop entityIds that's iterable!
   */
  const entityIds: string[] = entityId.split(',');

  // Data is the entrypoint for this entity in the Redux cache. It contains the loading state, error state, result set and everything else related
  // to the entity that we keep in cache. This does mean that even though data exists, it might not CONTAIN data yet!
  const data = delve(entitiesById, entityId);

  useEffect(() => {
    if (entityType === 'artist') {
      fetchSocialData(entityId);
    }
  }, [fetchSocialData, entityId, entityType]);

  useEffect(() => {
    const artistId = delve(data, 'entity.primaryArtist.id');
    if (artistId) {
      fetchArtistMetadata(artistId);
    }
  }, [data, fetchArtistMetadata]);

  const socialMetrics = useMemo(
    () => entityType === 'artist' && sortSocialMetrics(socialData.value),
    [entityType, socialData]
  );

  /** Only tracks and releases can be a versions */
  const canBeVersion = ['track', 'release'].includes(entityType);

  /** Render error page when the user visits a group route, but with the entityId of a version. */
  const isErrorPageShown =
    // Showing a single entity
    !compareMode &&
    // It's possible to be a version
    canBeVersion &&
    // It does not have a parent entity ID
    !parentEntityId &&
    // It is an entity though but...
    data?.entity &&
    // the entity is not  agroup
    !delve(data?.entity, 'is_group');

  useEffect(() => {
    if (!data || data?.loading || data?.error || isErrorPageShown) return;
    dispatch(trackViewedEntities({ id: entityId, type: entityType }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, entityId, entityType, data]);

  // Create an array based on the comma separated list in order to make an api call
  // When Entity is in Country compare mode countryIds is filled with an Array of country Ids.

  if (!data) {
    return null;
  }

  const { entity, error, errorInfo, status } = data;

  // Data could be preloaded without metadata
  const loading: boolean =
    data.loading &&
    !entity &&
    entityType !== 'artist' &&
    entityType !== 'label';

  /** Are we currently on a version Entity page */
  let isVersion = false;
  if (canBeVersion) {
    if (parentEntityId) {
      isVersion = true;
    }
    if (entity && 'parent_group_id' in entity && entity.parent_group_id) {
      isVersion = true;
    }
  }

  /**
   * Render error page instead of entity when the API returns an error after trying
   * to fetch critical entity data (e.g. metadata). It handles different error statuses,
   * for example 400, 404 and 500 errors.
   */
  if (!loading && error) {
    return <ErrorPage error={error} errorInfo={errorInfo} status={status} />;
  }

  if (isErrorPageShown) {
    return (
      // We really shouldn't render the page
      <ErrorPage error={new Error('This page does not exist.')} status={404} />
    );
  }

  //@TODO Not sure why we need delve here, shouldn't displayName be a given once the data is there?
  const entityTitle = entity && entity.displayName;

  //@ TODO Can we make sure this returns something valid, even if we pass data as undefined value? These conditionals seem like a codesmell to me
  const topbarTitle: ReactNode = getTopbarTitle(entityType, data);

  const artworkSrc: string = delve(entity, 'thumbnailUrl');

  /**
     * @TODO CHECK IF THIS WORKS AFTER THE REFACTOR PR MERGE
     * As long as there is loaded data, use the
        entityType from the API call, otherwise use the entityType from the URL.
        This is needed to make sure that the entityType doesn't immediately
        change when navigating to a different entity but can ease out before it
        changes. */
  let currentEntityType: EntityTypes = entityType;
  if (entity && !loading) {
    currentEntityType = apiTypeToEntityType(data.entity.type);
  }

  const currentEntityId: string =
    data && data.entity ? data.entity.id : entityId;
  const isTransitioning: boolean = !entity || entity.id !== entityId || loading;

  // Determine the path prefix
  const pathPrefix = getPathPrefix(compareMode, countryIds);

  const dataColors = compareMode ? compareColors : defaultColors.seriesColors;

  const customRegionFilter =
    getFiltersFromUrl(location)?.customRegion && !isArray(customRegions)
      ? getFiltersFromUrl(location)?.customRegion
      : undefined;

  const context: IEntity = {
    entityType: currentEntityType,
    entityIds,
    countryIds,
    compareMode,
    topic: subTopic ? [topic, subTopic] : [topic],
    parentEntityId,
    filters: getFiltersFromUrl(location),
    viewSettings: viewSettings,
    isVersion,
    dataColors,
    customRegionFilter,
  };

  return (
    <>
      <ScrollTopOnMount identifier={`${entityType}/${entityId}`} />
      <ErrorBoundary type="page">
        {entityTitle && (
          <Helmet
            defer={false}
            title={`${entityTitle} - ${capitalize(currentEntityType)}`}
          />
        )}
        <EntityContext.Provider value={context}>
          <InViewObserver
            render={(props: any): ReactNode => (
              <Header
                loading={loading}
                withVerticalBackground
                inView={props.inview}
              >
                <ImgCover>
                  <img src={`${artworkSrc}_640`} alt="" />
                </ImgCover>

                <TopBar isStatic />
                <TopBar
                  entityTitle={topbarTitle}
                  inView={props.inview}
                  isSticky
                  artworkSrc={artworkSrc}
                  productType={data?.entity?.productType}
                  noComparedItems={compareMode !== 'country'}
                  artists={artistMetadata?.value?.artists}
                  artistsFulfilled={artistMetadata?.fulfilled}
                  showOnlyOnScroll
                />

                {compareMode === 'country' && (
                  <CountryCompareHeader
                    entityType={entityType}
                    topic={context.topic}
                    history={history}
                    entityIds={entityIds}
                    countryIds={countryIds}
                    entitiesById={entitiesById}
                  />
                )}
                {entityType !== 'label' && entityType !== 'artist' && (
                  <Breadcrumbs
                    artists={artistMetadata?.value?.artists}
                    topic={context.topic}
                    loading={artistMetadata.pending}
                  />
                )}
                <EntityHeader
                  title={entityTitle}
                  entityId={currentEntityId}
                  artworkSrc={artworkSrc}
                  loading={isTransitioning}
                  data={data}
                  socialMetrics={socialMetrics}
                  artists={artistMetadata?.value?.artists}
                />
                {entityType !== 'label' && (
                  <TopicNavigation
                    activeTopic={topic}
                    pathPrefix={pathPrefix}
                  />
                )}
              </Header>
            )}
          />

          <NotificationSettingsModal />

          <Content>
            <EntityContent
              loading={isTransitioning}
              artistMetadata={artistMetadata?.value?.artists[0]?.artist}
            />
          </Content>
          <BackToTop />
          <Footer />
        </EntityContext.Provider>
      </ErrorBoundary>
    </>
  );
};

export default apiConnect((): any => ({
  fetchSocialData: (artistIds: string[]): any => ({
    socialData: getSocialKPIURL({ entityIds: artistIds }),
  }),
  fetchArtistMetadata: (artistId: any): any => ({
    artistMetadata: getAllowedRelationsURL({
      entityId: artistId,
      expand: 'none',
    }),
  }),
}))(Entity as any) as unknown as React.FC;
